import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { showToast } from '@/components/ui/show-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useResetPasswordMutation } from '@/services/auth/queries';
import { usePostHog } from 'posthog-js/react';

const formSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z
    .string()
    .min(8, { message: 'Password must be at least 8 characters' })
    .regex(/[A-Za-z]/, { message: 'Must contain at least one letter' })
    .regex(/[0-9]/, { message: 'Must contain at least one number' })
    .regex(/[^A-Za-z0-9]/, {
      message: 'Password must contain at least one special character'
    }),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type UserFormValue = z.infer<typeof formSchema>;

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const posthog = usePostHog();

  const form = useForm<UserFormValue>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: ''
    }
  });

  const { mutate, isPending } = useResetPasswordMutation(
    () => {
      posthog?.capture('form_submitted', { form_name: 'reset_password', success: true });
      showToast('success', 'Password reset successfully! You can now sign in with your new password.');
      window.location.href = '/sign-in';
    },
    (error) => {
      posthog?.capture('form_submitted', { form_name: 'reset_password', success: false });
      if (error.response?.status === 400) {
        showToast('error', 'Invalid or expired reset token');
      } else if (error.response?.status === 404) {
        showToast('error', 'Email not found');
      } else {
        showToast('error', 'Failed to reset password. Please try again.');
      }
    }
  );

  const onSubmit = (data: UserFormValue) => {
    if (!token) {
      showToast('error', 'Missing reset token');
      return;
    }
    
    mutate({
      email: data.email,
      newPassword: data.password,
      token
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='w-full space-y-4'
      >
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-sm font-bold'>Email</FormLabel>
              <FormControl>
                <Input
                  type='email'
                  placeholder='Enter your email...'
                  disabled={isPending}
                  className='h-[50px]'
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-sm font-bold'>New Password</FormLabel>
              <FormControl>
                <Input
                  type='password'
                  placeholder='Enter your new password...'
                  disabled={isPending}
                  className='h-[50px]'
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name='confirmPassword'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-sm font-bold'>Confirm Password</FormLabel>
              <FormControl>
                <Input
                  type='password'
                  placeholder='Confirm your new password...'
                  disabled={isPending}
                  className='h-[50px]'
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type='submit'
          disabled={isPending}
          className='h-12 w-full text-white'
          aria-label='Reset Password'
          trackingEvent='reset_password_submitted'
          trackingProperties={{ location: 'reset_password_page' }}
        >
          {isPending ? 'Resetting Password...' : 'Reset Password'}
        </Button>
      </form>

      <div className='mx-auto w-full pt-5 text-center'>
        <Link
          to={'/sign-in'}
          className='w-full text-primary transition-colors hover:text-primary/80'
        >
          Go back to Login
        </Link>
      </div>
    </Form>
  );
}